import { ApplicationState, ApplicationState as State } from '@/store';
import {
    ContactLinksFetchStartAction,
    EventLogByChannelNameStartAction,
    EventLogParams,
    EventLogParamsByChannelName,
    EventLogStartAction,
    EventLogTotalEventCounts,
    ExportEventsAction,
    ExportLinkContactsAction,
    ExportLinksAction,
    FetchChannelEventsCountsAction,
    GetLinkTrackingAbSplitStartAction,
    GetLinkTrackingListStartAction,
    GetReportsLocationStatsByNameStartAction,
    GetReportsSummaryStartAction,
    LinkContactsAllStartAction,
    LinkContactsStartAction,
    ReportsActionTypes,
    ReportsFetchStartAction,
    ReportsState,
    ResendStartAction,
    UrlSearchInProgressActiveParams,
    UrlSearchParams,
} from '@/store/reports/types';
import { DEFAULT_PER_PAGE_COUNT, SHORT_DATE_FORMAT } from '@/services/constants';
import {
    DeviceDetailsPaginationInterface,
    DevicesCampaignsFetchStartAction,
    DevicesFetchStartAction,
} from '@/store/reports/deviceDetails/types';
import {
    DevicesCampaignsFetchError,
    DevicesCampaignsFetchSuccess,
    DevicesFetchError,
    DevicesFetchSuccess,
    getDeviceDetailsStore,
} from '@/store/reports/deviceDetails/actions';
import { HideToast, ShowToast } from '@/store/toast/actions';
import { push as Push, replace } from 'redux-first-history';
import { call, delay, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { isNil, omit, omitBy } from 'lodash-es';
import { parse, stringify } from 'query-string';

import { ExportStatus } from '@dev/ee-api/ee-api-models';
import { ExportsPaths } from '@/services/dictionaries/Paths';
import { TotalEventHeaders } from '@/services/dictionaries/Headers';
import { calculateOffsetAndLimit } from '@/services/tools/handleUrlParams';
import { getUnsubscribeContactsStore } from '@/store/contacts/actions';
import { headersParser } from '@/services/tools/headersParser';
import { history } from '@shared-store/shared-store';
import i18n from '@/../i18n';
import { isEmpty } from 'lodash-es';
import { Logger } from '@/services/errors/appLogger';
import { errorToast } from './commonGenerators/errorToast';
import dayjs from '../services/tools/dayjs';
import {
    checkTaskStatus,
    exportEvents,
    exportLinkContacts,
    exportLinks,
    getLinkContacts,
    getLinktrackingsAbsplitByChannelName,
    getReportsLinkTrackings,
    getReportsLocationStatsByName,
    getReportsSummary,
    getUserAgentCampaignStats,
    getUserAgentStats,
    loadContactLinks,
    loadEventLog,
    loadEventLogByChannelName,
    loadEventLogsByChannelNameCounts,
    resendByMessageid,
} from '../services/ElasticApi';
import {
    ContactLinksLoadError,
    ContactLinksLoadSuccess,
    EventLogByChannelNameError,
    EventLogByChannelNameSuccess,
    EventLogError,
    EventLogSuccess,
    GetLinkTrackingAbSplitError,
    GetLinkTrackingAbSplitSuccess,
    GetLinkTrackingListError,
    GetLinkTrackingListSuccess,
    GetReportsLocationStatsByNameSuccess,
    GetReportsSummaryError,
    GetReportsSummarySuccess,
    LoadLinkContactsAllError,
    LoadLinkContactsAllSuccess,
    LoadLinkContactsError,
    LoadLinkContactsSuccess,
    ReportsLoadError,
    ReportsLoadSuccess,
    ResendError,
    ResendSuccess,
    UpdateChannelEventsCounts,
} from '../store/reports/actions';

const getLinkTrackingActivePage = (state: State): number => state.reports.linkTrackingActivePage;
const getLinkTrackingPerPage = (state: State): number => state.reports.linkTrackingPerPage;

function* handleURLQuery(activePage: number, perPage: number): any {
    let queryParams = parse(history.location.search);

    activePage < 2 ? (queryParams = omit(queryParams, 'page')) : (queryParams.page = activePage.toString());

    const maxElements = parseInt(queryParams.perPage as string, 10);

    if (maxElements !== perPage && perPage !== DEFAULT_PER_PAGE_COUNT) {
        queryParams.perPage = perPage.toString();
    }

    if (perPage === DEFAULT_PER_PAGE_COUNT) {
        queryParams = omit(queryParams, 'perPage');
    }

    yield put(Push({ search: stringify(queryParams) }));
}

function* loadReports(action: ReportsFetchStartAction): any {
    const { log }: ReportsState = yield select((store: ApplicationState) => store?.reports);
    const currentEventLog = yield select((store: ApplicationState) => store?.reports?.eventLog);

    const apiParams = action.payload.params;

    if (action.payload.params.offset > 0) {
        apiParams.to = dayjs.utc(currentEventLog?.Recipients?.at?.(-1)?.EventDate).toJSON();
    }

    try {
        const resp = yield call(loadEventLog, apiParams as any);
        const counts = Number(resp.headers[TotalEventHeaders.TotalCount]) || 0;

        let updatedLogs = resp.data;
        const hasMoreLogs = isEmpty(resp?.data?.Recipients)
            ? false
            : (resp?.data?.Recipients?.length || 0) % DEFAULT_PER_PAGE_COUNT === 0;

        if (action.payload.params.offset > 0 && action.payload?.params?.infinityScroll) {
            updatedLogs = { ...resp.data, Recipients: [...log.Recipients, ...(resp?.data?.Recipients || [])] };
        }
        yield put(ReportsLoadSuccess(updatedLogs, counts, action.payload.params.statuses, hasMoreLogs));
    } catch (e) {
        console.log(e);
        yield put(ReportsLoadError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadReports(): any {
    yield takeLatest(ReportsActionTypes.FETCH_START, loadReports);
}

const extractTotalEventCounts = (headers: any): EventLogTotalEventCounts => ({
    sending: Number(headers[TotalEventHeaders.SendingCount]) || 0,
    readytosend: Number(headers[TotalEventHeaders.ReadytosendCount]) || 0,
    waitingtoretry: Number(headers[TotalEventHeaders.WaitingtoretryCount]) || 0,
    all: Number(headers[TotalEventHeaders.AllCount]) || 0,
    error: Number(headers[TotalEventHeaders.ErrorCount]) || 0,
    sent: Number(headers[TotalEventHeaders.SentCount]) || 0,
    opened: Number(headers[TotalEventHeaders.OpenedCount]) || 0,
    clicked: Number(headers[TotalEventHeaders.ClickedCount]) || 0,
    unsubscribed: Number(headers[TotalEventHeaders.UnsubscribedCount]) || 0,
    abuseReport: Number(headers[TotalEventHeaders.AbuseReportCount]) || 0,
});

function* loadEventsLog(action: EventLogStartAction): any {
    try {
        const { payload } = action;
        const currentEventLog = yield select((store: ApplicationState) => store?.reports?.eventLog);
        const { offset, limit, statuses, messageCategory, email, channelName } = payload.params;
        const activePage = (offset + limit) / limit;

        let to = null;
        if (offset > 0) {
            to = dayjs.utc(currentEventLog?.Recipients?.at?.(-1)?.EventDate).toJSON();
        }

        const eventLogData = yield call(loadEventLog, {
            limit,
            to,
            // offset,
            channelName,
            statuses,
            messageCategory,
            email,
        });

        const eventsCount = extractTotalEventCounts(eventLogData.headers);

        const urlQueryParams: UrlSearchInProgressActiveParams = {
            page: activePage,
            perPage: limit,
            statuses,
        };

        if (messageCategory) {
            urlQueryParams.result = messageCategory;
        }

        if (email) {
            urlQueryParams.search = email;
        }

        yield put(Push({ search: stringify(urlQueryParams as any) }));

        const eventLogParams: EventLogParams = {
            offset,
            limit,
            statuses,
            messageCategory,
            email,
        };

        yield put(EventLogSuccess(eventLogData.data, eventsCount, null, eventLogParams));
    } catch (e) {
        console.log(e);
        yield put(EventLogError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadEventsLog(): any {
    yield takeLatest(ReportsActionTypes.EVENT_LOG_START, loadEventsLog);
}

function* loadEventsLogByChannelName(action: EventLogByChannelNameStartAction): any {
    try {
        const { payload } = action;
        const { offset, limit, statuses, messageCategory, email, channelName, lastEvent, from, to } = payload.params;

        const eventLogData = yield call(loadEventLogByChannelName, {
            limit,
            offset,
            channelName,
            statuses,
            messageCategory,
            lastEvent,
            email,
            from,
            to,
        });

        const totalCount = +eventLogData.headers['x-total-count'] || 0;

        const eventLogParams: EventLogParamsByChannelName = {
            offset,
            limit,
            statuses,
            messageCategory,
            email,
            lastEvent,
            channelName,
            from,
            to,
        };
        yield put(EventLogByChannelNameSuccess(eventLogData.data, totalCount, null, eventLogParams));
    } catch (e) {
        console.log(e);
        yield put(EventLogByChannelNameError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadEventsLogByChannelName(): any {
    yield takeLatest(ReportsActionTypes.EVENT_LOG_BY_CHANNELNAME_START, loadEventsLogByChannelName);
}

function* loadContactLinkClicks(action: ContactLinksFetchStartAction): any {
    try {
        const links = yield call(loadContactLinks, action.payload.params);
        const counts = Number(links.headers[TotalEventHeaders.TotalCount]) || 0;

        yield put(ContactLinksLoadSuccess(links.data, counts));
    } catch (e) {
        console.log(e);
        yield put(ContactLinksLoadError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadLinkClicks(): any {
    yield takeLatest(ReportsActionTypes.FETCH_CONTACT_LINKS_START, loadContactLinkClicks);
}

function* loadReportsSummary(action: GetReportsSummaryStartAction): any {
    try {
        const { payload } = action;
        const params = {
            from: payload.from,
            to: payload.to,
            channelName: payload.channelName,
            interval: payload.interval,
            transactionID: payload.transactionID,
        };

        const summary = yield call(getReportsSummary, params);
        yield put(GetReportsSummarySuccess(summary?.data));
    } catch {
        yield put(GetReportsSummaryError());
    }
}
function* watchLoadReportsSummary(): any {
    yield takeLatest(ReportsActionTypes.GET_REPORTS_SUMMARY_START, loadReportsSummary);
}

function* GetLinkTrackingList(action: GetLinkTrackingListStartAction): any {
    try {
        const { payload } = action;
        const {
            activePage = yield select(getLinkTrackingActivePage),
            perPage = yield select(getLinkTrackingPerPage),
            isRequiredFullDateRange,
        } = payload;

        if (isRequiredFullDateRange) {
            yield handleURLQuery(activePage, perPage);
        }

        const { offset, limit } = calculateOffsetAndLimit(activePage, perPage);

        const linkTracking = yield call(getReportsLinkTrackings, {
            ...(isRequiredFullDateRange ? {} : { limit }),
            ...(isRequiredFullDateRange ? {} : { offset }),
            channelName: payload.channelName,
            linkSearch: payload.linkSearch,
            ...(isRequiredFullDateRange ? { from: payload.from } : {}),
            ...(isRequiredFullDateRange ? { to: payload.to } : {}),
        });

        const totalLinkCount = parseInt(linkTracking.headers[TotalEventHeaders.TotalCount], 10) || 0;

        yield put(GetLinkTrackingListSuccess(linkTracking?.data, totalLinkCount, activePage, perPage));
    } catch {
        yield put(GetLinkTrackingListError());
    }
}

function* watchGetLinkTrackingList(): any {
    yield takeLatest(ReportsActionTypes.LOAD_LINK_TRACKING_START, GetLinkTrackingList);
}

function* exportLink(action: ExportLinksAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    try {
        const resp = yield call(exportLinks, payload);

        while (checkExportStatus) {
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }

            delay(500);
        }

        yield put(Push(ExportsPaths.Exports));

        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('linksexported'),
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* watchExportLinks(): any {
    yield takeLatest(ReportsActionTypes.EXPORT_LINKS, exportLink);
}

function* GetLinkTrackingAbSplit(action: GetLinkTrackingAbSplitStartAction): any {
    try {
        const { payload } = action;
        const { from, to, channelName, activePage, perPage, isUpdateUrlRequired } = payload;

        const { offset, limit } = calculateOffsetAndLimit(activePage, perPage);
        const abSplitLinksList = yield call(getLinktrackingsAbsplitByChannelName, {
            channelName,
            offset,
            limit,
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
        });

        const totalCount = parseInt(abSplitLinksList.headers[TotalEventHeaders.TotalCount], 10) || 0;

        if (isUpdateUrlRequired) {
            const urlQueryParams: UrlSearchParams = {
                page: activePage,
                perPage,
            };

            yield put(Push({ search: stringify(urlQueryParams as any) }));
        }

        yield put(GetLinkTrackingAbSplitSuccess(abSplitLinksList?.data, totalCount, offset, limit));
    } catch (err) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${err.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(GetLinkTrackingAbSplitError());
    }
}
function* watchGetLinkTrackingAbSplit(): any {
    yield takeLatest(ReportsActionTypes.LOAD_LINK_TRACKING_ABSPLIT_START, GetLinkTrackingAbSplit);
}

function* fetchLinkContactsList(action: LinkContactsStartAction): any {
    try {
        const { payload } = action;
        const { from, to, activePage, perPage } = payload;
        const { offset, limit } = calculateOffsetAndLimit(activePage, perPage);
        const linkContactsList = yield call(getLinkContacts, {
            limit,
            offset,
            channelName: payload.channelName,
            link: payload.link,
            from,
            to,
        });

        const totalLinkContactsCount = parseInt(linkContactsList.headers[TotalEventHeaders.TotalCount], 10) || 0;

        const urlQueryParams: UrlSearchParams = {
            page: activePage,
            perPage,
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            ...(to ? { to: dayjs(to).format(SHORT_DATE_FORMAT) } : {}),
        };

        yield put(replace({ search: stringify(urlQueryParams as any) }));

        const linkContactsPagination = {
            activePage,
            perPage: limit,
            from,
            to,
        };

        yield put(LoadLinkContactsSuccess(linkContactsList?.data, totalLinkContactsCount, linkContactsPagination));
    } catch (err) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${err.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(LoadLinkContactsError());
    }
}
function* watchLinkContactsList(): any {
    yield takeLatest(ReportsActionTypes.LINK_CONTACTS_START, fetchLinkContactsList);
}

function* fetchLinkContactsAllList(action: LinkContactsAllStartAction): any {
    try {
        const { payload } = action;
        const { from, to } = payload;

        const linkContactsAllList = yield call(getLinkContacts, {
            channelName: payload.channelName,
            link: payload.link,
            from,
            to,
        });

        yield put(LoadLinkContactsAllSuccess(linkContactsAllList?.data));
    } catch (err) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${err.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(LoadLinkContactsAllError());
    }
}
function* watchLinkContactsAllList(): any {
    yield takeLatest(ReportsActionTypes.LINK_CONTACTS_ALL_START, fetchLinkContactsAllList);
}

function* exportLinkContact(action: ExportLinkContactsAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    try {
        const resp = yield call(exportLinkContacts, payload);

        while (checkExportStatus) {
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }

            delay(500);
        }

        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('contactsexported'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(Push(ExportsPaths.Exports));
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* watchExportLinkContacts(): any {
    yield takeLatest(ReportsActionTypes.EXPORT_LINK_CONTACTS, exportLinkContact);
}

function* loadLocationStatsByName(action: GetReportsLocationStatsByNameStartAction): any {
    const { payload } = action;

    try {
        const resp = yield call(getReportsLocationStatsByName, payload);
        const totalCount = Number(resp.headers[TotalEventHeaders.TotalCount]) || 0;
        const response = { data: resp.data, count: totalCount };

        const { isDisabledAddingPaginationParams } = action.payload;

        if (!isDisabledAddingPaginationParams) {
            const { offset, limit } = payload;
            const activePage = (offset + limit) / limit;

            const urlQueryParams: UrlSearchParams = {
                page: activePage,
                perPage: limit,
            };

            if (activePage) {
                yield put(Push({ search: stringify(urlQueryParams as any) }));
            }
        }

        yield put(GetReportsLocationStatsByNameSuccess(response));
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchLoadLocationStatsByName(): any {
    yield takeLatest(ReportsActionTypes.GET_REPORTS_LOCATION_STATS_BY_NAME_START, loadLocationStatsByName);
}

function* exportEvent(action: ExportEventsAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    try {
        const resp = yield call(exportEvents, payload);

        while (checkExportStatus) {
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }

            delay(500);
        }

        yield put(
            ShowToast({
                severity: 'info',
                message: i18n.t('eventsexported'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(Push(ExportsPaths.Exports));
    } catch (e) {
        Logger.error(e);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}
function* watchExportEvents(): any {
    yield takeLatest(ReportsActionTypes.EXPORT_EVENTS, exportEvent);
}

function* resend(action: ResendStartAction): any {
    const { payload } = action;

    try {
        yield call(resendByMessageid, { ...payload.params });
        yield put(
            ShowToast({
                severity: 'success',
                message: i18n.t('youremailsubmittedsuccessfully'),
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(ResendSuccess());
    } catch (e) {
        yield put(ResendError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchResend(): any {
    yield takeLatest(ReportsActionTypes.RESEND_START, resend);
}

function* fetchChannelEventsCount(action: FetchChannelEventsCountsAction): any {
    try {
        const { payload } = action;

        const params = {
            from: payload.from,
            to: payload.to,
            channelName: payload.channelName,
            email: payload.email,
            lastEvent: payload.lastEvent,
        };

        const filteredParams = omitBy(params, isNil) as EventLogParamsByChannelName;

        const logs = yield call(loadEventLogsByChannelNameCounts, filteredParams);

        const updatedCounts = headersParser(logs?.headers);
        yield put(UpdateChannelEventsCounts(updatedCounts));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* watchForFetchChannelEventsCount(): any {
    yield takeEvery(ReportsActionTypes.FETCH_CHANNEL_EVENTS_COUNTS, fetchChannelEventsCount);
}

function* fetchDeviceDetailsStats(action: DevicesFetchStartAction): any {
    const { from, to } = action.payload;

    try {
        const response = yield call(getUserAgentStats, { from, to });
        const deviceDetailsStats = response.data;
        const { deviceDetails } = yield select(getUnsubscribeContactsStore);

        const urlQueryString: DeviceDetailsPaginationInterface = {
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            to: dayjs(to).format(SHORT_DATE_FORMAT),
        };
        yield put(Push({ search: stringify(urlQueryString as any) }));

        const updatedDeviceDetailsStats = {
            ...deviceDetails,
            deviceDetailsStats,
            pagination: { ...urlQueryString },
        };
        yield put(DevicesFetchSuccess(updatedDeviceDetailsStats));
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 3000,
            })
        );
        yield put(DevicesFetchError());
    }
}

function* watchDeviceDetails(): any {
    yield takeLatest(ReportsActionTypes.GET_DEVICE_DETAILS, fetchDeviceDetailsStats);
}

function* fetchDeviceDetailsCampaignStats(action: DevicesCampaignsFetchStartAction): any {
    const { from, to, campaignName, channelName } = action.payload;

    try {
        const response = yield call(getUserAgentCampaignStats, { from, to, campaignName: campaignName || channelName });
        const deviceDetailsStats = response.data;
        const { deviceDetails } = yield select(getDeviceDetailsStore);

        const urlQueryString: DeviceDetailsPaginationInterface = {
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            to: dayjs(to).format(SHORT_DATE_FORMAT),
            ...(campaignName ? { campaignName } : {}),
            ...(channelName ? { channelName } : {}),
        };
        yield put(Push({ search: stringify(urlQueryString as any) }));

        const updatedDeviceDetailsStats = {
            ...deviceDetails,
            deviceDetailsStats,
            pagination: { ...urlQueryString },
        };

        yield put(DevicesCampaignsFetchSuccess(updatedDeviceDetailsStats));
    } catch (e) {
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error ?? e.message}`,
                duration: 3000,
            })
        );
        yield put(DevicesCampaignsFetchError());
    }
}

function* watchDeviceDetailsChannel(): any {
    yield takeLatest(ReportsActionTypes.GET_DEVICE_DETAILS_CAMPAIGNS, fetchDeviceDetailsCampaignStats);
}

const reportSagas = [
    fork(watchLoadEventsLog),
    fork(watchLoadReports),
    fork(watchLoadLinkClicks),
    fork(watchLoadReportsSummary),
    fork(watchGetLinkTrackingList),
    fork(watchExportLinks),
    fork(watchLinkContactsList),
    fork(watchExportLinkContacts),
    fork(watchLoadLocationStatsByName),
    fork(watchGetLinkTrackingAbSplit),
    fork(watchLinkContactsAllList),
    fork(watchExportEvents),
    fork(watchLoadEventsLogByChannelName),
    fork(watchResend),
    fork(watchForFetchChannelEventsCount),
    fork(watchDeviceDetails),
    fork(watchDeviceDetailsChannel),
];

export default reportSagas;
